import { NotificationPriority, NotificationType } from '../types/notification';

export interface SoundConfig {
  enabled: boolean;
  volume: number;
  priority: {
    [NotificationPriority.LOW]: boolean;
    [NotificationPriority.MEDIUM]: boolean;
    [NotificationPriority.HIGH]: boolean;
    [NotificationPriority.URGENT]: boolean;
  };
  types: {
    [NotificationType.BOOKING]: boolean;
    [NotificationType.PAYMENT]: boolean;
    [NotificationType.FLEET]: boolean;
    [NotificationType.SYSTEM]: boolean;
    [NotificationType.USER]: boolean;
    [NotificationType.BLOG]: boolean;
    [NotificationType.MAINTENANCE]: boolean;
    [NotificationType.SECURITY]: boolean;
  };
}

class SoundService {
  private audioContext: AudioContext | null = null;
  private config: SoundConfig;

  constructor() {
    this.config = this.loadConfig();
    this.initializeAudioContext();
  }

  private loadConfig(): SoundConfig {
    const saved = localStorage.getItem('notificationSoundConfig');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      volume: 0.5,
      priority: {
        [NotificationPriority.LOW]: true,
        [NotificationPriority.MEDIUM]: true,
        [NotificationPriority.HIGH]: true,
        [NotificationPriority.URGENT]: true,
      },
      types: {
        [NotificationType.BOOKING]: true,
        [NotificationType.PAYMENT]: true,
        [NotificationType.FLEET]: true,
        [NotificationType.SYSTEM]: true,
        [NotificationType.USER]: true,
        [NotificationType.BLOG]: true,
        [NotificationType.MAINTENANCE]: true,
        [NotificationType.SECURITY]: true,
      }
    };
  }

  private saveConfig(): void {
    localStorage.setItem('notificationSoundConfig', JSON.stringify(this.config));
  }

  private async initializeAudioContext(): Promise<void> {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume audio context if it's suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    } catch (error) {
      console.warn('Failed to initialize audio context:', error);
    }
  }

  // Play notification sound based on type and priority
  async playNotificationSound(type: NotificationType, priority: NotificationPriority): Promise<void> {
    if (!this.config.enabled || !this.config.types[type] || !this.config.priority[priority]) {
      return;
    }

    if (!this.audioContext) {
      await this.initializeAudioContext();
    }

    if (!this.audioContext) {
      console.warn('Audio context not available');
      return;
    }

    try {
      const soundPattern = this.getSoundPattern(type, priority);
      await this.playSound(soundPattern);
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }

  private getSoundPattern(type: NotificationType, priority: NotificationPriority): SoundPattern {
    // Different sound patterns for different types and priorities
    const baseFrequency = this.getBaseFrequency(type);
    const pattern = this.getPriorityPattern(priority);
    
    return {
      frequency: baseFrequency,
      pattern: pattern,
      volume: this.config.volume
    };
  }

  private getBaseFrequency(type: NotificationType): number {
    switch (type) {
      case NotificationType.BOOKING:
        return 800; // Higher pitch for bookings
      case NotificationType.PAYMENT:
        return 600; // Medium pitch for payments
      case NotificationType.SECURITY:
        return 400; // Lower pitch for security alerts
      case NotificationType.MAINTENANCE:
        return 500; // Medium-low for maintenance
      case NotificationType.FLEET:
        return 700; // Medium-high for fleet
      case NotificationType.SYSTEM:
        return 550; // Medium for system
      case NotificationType.BLOG:
        return 650; // Medium-high for blog
      case NotificationType.USER:
        return 750; // High for user actions
      default:
        return 600;
    }
  }

  private getPriorityPattern(priority: NotificationPriority): SoundNote[] {
    switch (priority) {
      case NotificationPriority.URGENT:
        return [
          { duration: 0.1, pause: 0.05 },
          { duration: 0.1, pause: 0.05 },
          { duration: 0.1, pause: 0.05 },
          { duration: 0.2, pause: 0 }
        ]; // Rapid beeps
      case NotificationPriority.HIGH:
        return [
          { duration: 0.15, pause: 0.1 },
          { duration: 0.15, pause: 0 }
        ]; // Two beeps
      case NotificationPriority.MEDIUM:
        return [
          { duration: 0.2, pause: 0 }
        ]; // Single beep
      case NotificationPriority.LOW:
        return [
          { duration: 0.3, pause: 0 }
        ]; // Longer single beep
      default:
        return [{ duration: 0.2, pause: 0 }];
    }
  }

  private async playSound(pattern: SoundPattern): Promise<void> {
    if (!this.audioContext) return;

    const { frequency, pattern: notes, volume } = pattern;
    let currentTime = this.audioContext.currentTime;

    for (const note of notes) {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Set frequency with slight variation for more pleasant sound
      oscillator.frequency.setValueAtTime(frequency, currentTime);
      oscillator.frequency.setValueAtTime(frequency * 0.8, currentTime + note.duration * 0.5);

      // Set volume envelope
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + note.duration);

      oscillator.start(currentTime);
      oscillator.stop(currentTime + note.duration);

      currentTime += note.duration + note.pause;
    }
  }

  // Test sound functionality
  async testSound(type: NotificationType, priority: NotificationPriority): Promise<void> {
    await this.playNotificationSound(type, priority);
  }

  // Update sound configuration
  updateConfig(newConfig: Partial<SoundConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();
  }

  // Get current configuration
  getConfig(): SoundConfig {
    return { ...this.config };
  }

  // Enable/disable sounds
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.saveConfig();
  }

  // Set volume (0-1)
  setVolume(volume: number): void {
    this.config.volume = Math.max(0, Math.min(1, volume));
    this.saveConfig();
  }

  // Enable/disable sounds for specific type
  setTypeEnabled(type: NotificationType, enabled: boolean): void {
    this.config.types[type] = enabled;
    this.saveConfig();
  }

  // Enable/disable sounds for specific priority
  setPriorityEnabled(priority: NotificationPriority, enabled: boolean): void {
    this.config.priority[priority] = enabled;
    this.saveConfig();
  }
}

interface SoundPattern {
  frequency: number;
  pattern: SoundNote[];
  volume: number;
}

interface SoundNote {
  duration: number; // in seconds
  pause: number; // pause after note in seconds
}

export const soundService = new SoundService();